import styled from "styled-components";

/**
 * PWA INSTALL BANNER — Branded, non-intrusive banner below header.
 * Replaces the default Ant Design notification.info with a
 * custom component matching the design system.
 * Slides down with animation, includes description + branded CTA.
 *
 * Stoji u toku dokumenta, odmah ispod hedera, pa gura sadržaj naniže. Ranije je
 * bio `position: fixed; top: var(--header-height)` i time trajno prekrivao prvih
 * ~70px sadržaja na svakoj stranici — najuočljivije na potvrdi o zakazivanju,
 * gde je sekao vrh kartice tako da je izgledalo kao negativan `margin-top`.
 */
const Banner = styled.div`
  background: var(--color-bg-elevated);
  border-bottom: 1px solid var(--color-border);
  box-shadow: var(--shadow-md);
  animation: pwaBannerSlideIn var(--duration-slow) var(--ease-out) both;

  @keyframes pwaBannerSlideIn {
    from {
      opacity: 0;
      transform: translateY(-100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Content = styled.div`
  max-width: var(--content-max-width);
  margin: 0 auto;
  padding: var(--space-4) var(--space-5);
  display: flex;
  align-items: center;
  gap: var(--space-4);

  @media (max-width: 767px) {
    padding: var(--space-3) var(--space-4);
    gap: var(--space-3);
  }
`;

const Icon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  background: var(--color-primary-light);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--color-primary);
  font-size: 20px;

  @media (max-width: 767px) {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }
`;

const Text = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-family: var(--font-display);
  font-size: var(--text-base);
  font-weight: var(--weight-semibold);
  color: var(--color-text);
  line-height: var(--leading-snug);

  @media (max-width: 767px) {
    font-size: var(--text-sm);
  }
`;

const Description = styled.div`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  line-height: var(--leading-normal);
  margin-top: 2px;

  @media (max-width: 767px) {
    font-size: var(--text-xs);
  }
`;

const GuideLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-left: var(--space-1);
  color: var(--color-primary);
  font-size: inherit;
  font-weight: var(--weight-semibold);
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
`;

const InstallBtn = styled.button`
  height: 36px;
  padding: 0 var(--space-5);
  border-radius: var(--radius-md);
  background: var(--color-gradient);
  color: var(--color-text-on-primary);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  border: none;
  cursor: pointer;
  transition: var(--transition-all);
  white-space: nowrap;
  box-shadow: var(--shadow-primary);

  &:hover {
    background: var(--color-gradient-hover);
    box-shadow: var(--shadow-primary-lg);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 767px) {
    height: 34px;
    padding: 0 var(--space-4);
    font-size: var(--text-xs);
  }
`;

const DismissBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: var(--radius-full);
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);
  font-size: 14px;
  transition: var(--transition-colors);

  &:hover {
    background: var(--color-bg-muted);
    color: var(--color-text);
  }
`;

export default {
  Banner,
  Content,
  Icon,
  Text,
  Title,
  Description,
  GuideLink,
  Actions,
  InstallBtn,
  DismissBtn,
};
